import { money } from "@/lib/format";
import {
  channelCompensationCapabilities,
  composeCompensation,
  HISTORICAL_REFUND_AUTHORITY_COPY,
  remainingRefundableQty,
  remainingShippingAmount,
  type CompensationMethod,
  type RefundLineState,
  type UnfulfilledRefundDisposition,
  REFUND_AND_CANCEL_COPY,
  REFUND_INVENTORY_NOTICE,
  REFUND_ONLY_COPY,
  RETURN_REFUND_OPTIONAL_COPY,
  requiresUnfulfilledDisposition,
} from "@/lib/reverse-lifecycle";

export type ReturnComposerContext = {
  returnNumber: string;
  status: string;
  disposition: string | null;
  returnedQtyByLine: Record<string, number>;
};

type Props = {
  currency: string;
  chargedTotal: unknown;
  alreadyRefunded: unknown;
  tax?: unknown;
  orderShipping?: unknown;
  shippingAlreadyRefunded?: unknown;
  lines: RefundLineState[];
  qtyByLine: Record<string, number>;
  onQtyChange: (salesOrderItemId: string, qty: number) => void;
  includeShipping: boolean;
  onIncludeShippingChange: (value: boolean) => void;
  adjustmentAmount: string;
  onAdjustmentAmountChange: (value: string) => void;
  adjustmentReason: string;
  onAdjustmentReasonChange: (value: string) => void;
  reason: string;
  onReasonChange: (value: string) => void;
  method: CompensationMethod;
  onMethodChange: (value: CompensationMethod) => void;
  provider: string | null;
  returnContext?: ReturnComposerContext | null;
  syncProvider: boolean;
  onSyncProviderChange: (value: boolean) => void;
  allowProviderSync: boolean;
  unfulfilledRefundQty: number;
  disposition: UnfulfilledRefundDisposition | "";
  onDispositionChange: (value: UnfulfilledRefundDisposition) => void;
  productMasterPrice?: unknown;
};

export function RefundComposer(props: Props) {
  const capabilities = channelCompensationCapabilities(props.provider);
  const remainingShip = remainingShippingAmount(props.orderShipping, props.shippingAlreadyRefunded);
  const selections = Object.entries(props.qtyByLine).map(([salesOrderItemId, qty]) => ({ salesOrderItemId, qty }));
  const composed = composeCompensation({
    chargedTotal: props.chargedTotal,
    alreadyRefunded: props.alreadyRefunded,
    tax: props.tax,
    orderShipping: props.orderShipping,
    shippingAlreadyRefunded: props.shippingAlreadyRefunded,
    lines: props.lines,
    selections,
    includeShipping: props.includeShipping,
    adjustmentAmount: props.adjustmentAmount,
    adjustmentReason: props.adjustmentReason,
    method: props.method,
    provider: props.provider,
    productMasterPrice: props.productMasterPrice,
  });
  const quote = composed.ok ? composed.quote : null;

  return (
    <div className="space-y-4">
      {props.returnContext && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
          <div className="font-medium text-slate-900">Return {props.returnContext.returnNumber}</div>
          <div className="text-xs text-slate-500 mt-1">
            {props.returnContext.status}
            {props.returnContext.disposition ? ` · Disposition: ${props.returnContext.disposition === "restock" ? "RESTOCK" : props.returnContext.disposition}` : ""}
          </div>
          <p className="text-xs text-slate-500 mt-2">{RETURN_REFUND_OPTIONAL_COPY} Physical return status does not set the refund amount.</p>
        </div>
      )}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        {HISTORICAL_REFUND_AUTHORITY_COPY} {REFUND_INVENTORY_NOTICE}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <div>Original charged<div className="font-semibold">{money(props.chargedTotal, props.currency)}</div></div>
        <div>Previously refunded<div className="font-semibold">{money(props.alreadyRefunded, props.currency)}</div></div>
        <div>Max remaining<div className="font-semibold">{money(quote?.remainingRefundable ?? 0, props.currency)}</div></div>
        <div>GST included<div className="font-semibold">{money(props.tax, props.currency)}</div></div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2">SKU</th>
              <th className="text-right px-3 py-2">Ordered</th>
              {props.returnContext ? <th className="text-right px-3 py-2">Returned</th> : null}
              <th className="text-right px-3 py-2">Already refunded</th>
              <th className="text-right px-3 py-2">Eligible qty</th>
              <th className="text-right px-3 py-2">Qty to refund</th>
              <th className="text-right px-3 py-2">Historical paid</th>
              <th className="text-right px-3 py-2">Merchandise refund</th>
            </tr>
          </thead>
          <tbody>
            {props.lines.map((line) => {
              const refundable = remainingRefundableQty(line.qtyOrdered, line.qtyAlreadyRefunded);
              const returned = props.returnContext?.returnedQtyByLine[line.salesOrderItemId] ?? 0;
              const previewLine = quote?.lines.find((l) => l.salesOrderItemId === line.salesOrderItemId);
              return (
                <tr key={line.salesOrderItemId} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{line.productName ?? "—"}</div>
                    {line.variantLabel ? <div className="text-xs text-slate-500">{line.variantLabel}</div> : null}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{line.sku ?? "—"}</td>
                  <td className="px-3 py-2 text-right">{line.qtyOrdered}</td>
                  {props.returnContext ? <td className="px-3 py-2 text-right">{returned}</td> : null}
                  <td className="px-3 py-2 text-right">{line.qtyAlreadyRefunded}</td>
                  <td className="px-3 py-2 text-right">{refundable}</td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min={0}
                      max={refundable}
                      step={1}
                      value={props.qtyByLine[line.salesOrderItemId] ?? 0}
                      onChange={(e) => props.onQtyChange(line.salesOrderItemId, Number(e.target.value))}
                      className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-sm text-right"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">{money(line.lineCharged, props.currency)}</td>
                  <td className="px-3 py-2 text-right font-medium">{money(previewLine?.amount ?? 0, props.currency)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="text-sm font-medium text-slate-800">Shipping</div>
        <div className="grid grid-cols-3 gap-2 text-xs text-slate-600">
          <div>Original charged<div className="font-semibold text-slate-900">{money(props.orderShipping, props.currency)}</div></div>
          <div>Already refunded<div className="font-semibold text-slate-900">{money(props.shippingAlreadyRefunded, props.currency)}</div></div>
          <div>Remaining<div className="font-semibold text-slate-900">{money(remainingShip, props.currency)}</div></div>
        </div>
        {remainingShip > 0 ? (
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={props.includeShipping} onChange={(e) => props.onIncludeShippingChange(e.target.checked)} className="mt-1" />
            Refund remaining shipping {money(remainingShip, props.currency)}. Shipping is not included automatically because an item was returned.
          </label>
        ) : (
          <p className="text-xs text-slate-500">No remaining refundable shipping on this historical order.</p>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="text-sm font-medium text-slate-800">Other adjustment</div>
        <p className="text-xs text-slate-500">Goodwill, price correction, or service adjustment. This is not a product quantity refund.</p>
        <input
          type="number"
          min="0"
          step="0.01"
          value={props.adjustmentAmount}
          onChange={(e) => props.onAdjustmentAmountChange(e.target.value)}
          placeholder="0.00"
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
        />
        <input
          type="text"
          value={props.adjustmentReason}
          onChange={(e) => props.onAdjustmentReasonChange(e.target.value)}
          placeholder="Adjustment reason / note"
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
          maxLength={200}
        />
      </div>
      <div className="border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="text-sm font-medium text-slate-800">Compensation method</div>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input type="radio" checked={props.method === "original_payment"} onChange={() => props.onMethodChange("original_payment")} className="mt-1" />
          Refund to original payment method
        </label>
        {capabilities.methods.includes("store_credit") ? (
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="radio" checked={props.method === "store_credit"} onChange={() => props.onMethodChange("store_credit")} className="mt-1" />
            Store credit
          </label>
        ) : (
          <p className="text-xs text-slate-500">Store credit is not available. {capabilities.storeCreditReason}</p>
        )}
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1">
        <div className="font-medium text-slate-800">Total compensation</div>
        {quote ? (
          <>
            <div>Merchandise refund {money(quote.merchandise, props.currency)}</div>
            <div>+ Shipping refund {money(quote.shipping, props.currency)}</div>
            <div>+ Other adjustment {money(quote.adjustment, props.currency)}</div>
            <div className="font-semibold">TOTAL {money(quote.amount, props.currency)}</div>
            <div className="text-xs text-slate-500 pt-1">Remaining after this compensation {money(quote.remainingAfterThis, props.currency)}</div>
            <div className="text-xs text-slate-500">GST component is already included and is not added again ({money(quote.taxComponent, props.currency)}).</div>
          </>
        ) : (
          <div className="text-amber-800 text-xs">{composed.ok === false ? composed.message : "Select merchandise, shipping, or an adjustment."}</div>
        )}
      </div>
      {requiresUnfulfilledDisposition(props.unfulfilledRefundQty) && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-slate-700">What should happen to unfulfilled refunded quantity?</div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="radio" name="refund-disposition" checked={props.disposition === "refund_only"} onChange={() => props.onDispositionChange("refund_only")} className="mt-1" />
            <span><span className="font-medium">Refund only.</span> {REFUND_ONLY_COPY}</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="radio" name="refund-disposition" checked={props.disposition === "refund_and_cancel_unfulfilled"} onChange={() => props.onDispositionChange("refund_and_cancel_unfulfilled")} className="mt-1" />
            <span><span className="font-medium">Refund and cancel quantity.</span> {REFUND_AND_CANCEL_COPY}</span>
          </label>
        </div>
      )}
      <label className="block text-xs font-medium text-slate-500">Reason</label>
      <input type="text" value={props.reason} onChange={(e) => props.onReasonChange(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" maxLength={200} />
      {props.allowProviderSync && (
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={props.syncProvider} onChange={(e) => props.onSyncProviderChange(e.target.checked)} className="mt-1" />
          Also issue this refund on the channel after confirmation. Leave unchecked to record the Tradivo compensation only.
        </label>
      )}
    </div>
  );
}
